import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUtensils, 
  faUserTie, 
  faChartLine, 
  faArrowRight 
} from '@fortawesome/free-solid-svg-icons';
import gsap from 'gsap';
import './PortalSelector.css';
import logo from "../../assets/logo.svg";

const PortalSelector = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const cardsRef = useRef([]);
  const headerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Entry animations
      gsap.from(".ps-header", {
        y: -100,
        opacity: 0,
        duration: 1.2,
        ease: "power4.out"
      });

      gsap.from(".ps-card", {
        y: 100,
        opacity: 0,
        duration: 1,
        stagger: 0.2,
        ease: "back.out(1.7)",
        delay: 0.5,
        clearProps: "all"
      });

      // Floating background particles animation
      gsap.to(".ps-floating-blob", {
        x: "random(-50, 50)",
        y: "random(-50, 50)",
        duration: "random(10, 20)",
        repeat: -1,
        yoyo: true,
        ease: "none",
        stagger: {
          each: 2,
          from: "random"
        }
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const xPos = (clientX / window.innerWidth - 0.5) * 30;
    const yPos = (clientY / window.innerHeight - 0.5) * 30;

    gsap.to(".ps-ambient-bg", {
      x: xPos,
      y: yPos,
      duration: 1,
      ease: "power2.out"
    });
  };

  const portals = [
    {
      id: 'user',
      title: 'Diner',
      tagline: 'Gourmet Experience',
      description: 'Book tables, explore menus, and enjoy exclusive culinary rewards.',
      icon: faUtensils,
      color: '#ff4135',
      bg: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'staff',
      title: 'Staff',
      tagline: 'Operational Excellence',
      description: 'Streamline service, manage orders, and coordinate with the kitchen.',
      icon: faUserTie,
      color: '#00d1b2',
      bg: 'https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'admin',
      title: 'Manager',
      tagline: 'Business Intelligence',
      description: 'Control operations, analyze performance, and grow your restaurant business.',
      icon: faChartLine,
      color: '#f39c12',
      bg: 'https://images.unsplash.com/photo-1551288049-bbbda536339a?auto=format&fit=crop&w=800&q=80'
    }
  ];

  return (
    <div className="ps-container" ref={containerRef} onMouseMove={handleMouseMove}>
      <div className="ps-noise-overlay"></div>
      <div className="ps-ambient-bg"></div>
      <div className="ps-floating-blob ps-blob-1"></div>
      <div className="ps-floating-blob ps-blob-2"></div>
      <div className="ps-floating-blob ps-blob-3"></div>
      
      <header className="ps-header" ref={headerRef}>
        <div className="ps-logo-wrapper">
          <img src={logo} alt="TastyFlow" className="ps-logo" />
        </div>
        <p className="ps-subtitle">The ultimate doorway to modern gastronomy</p>
      </header>

      <div className="ps-grid">
        {portals.map((portal, i) => (
          <div 
            key={portal.id} 
            className="ps-card"
            ref={el => cardsRef.current[i] = el}
            onClick={() => navigate(`/login/${portal.id}`)}
          >
            <div className="ps-card-bg" style={{ backgroundImage: `url(${portal.bg})` }}></div>
            <div className="ps-card-overlay"></div>
            
            <div className="ps-card-content">
              <div className="ps-card-top">
                <div className="ps-card-icon" style={{ backgroundColor: portal.color }}>
                  <FontAwesomeIcon icon={portal.icon} />
                </div>
                <span className="ps-card-tag">{portal.tagline}</span>
              </div>
              
              <div className="ps-card-info">
                <h2 className="ps-card-title">{portal.title}</h2>
                <p className="ps-card-desc">{portal.description}</p>
                
                <button className="ps-card-btn">
                  Enter Portal <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <footer className="ps-footer">
        <p>© 2024 TastyFlow. Engineered for excellence.</p>
      </footer>
    </div>
  );
};

export default PortalSelector;
